import { AxiosError } from 'axios'
import { Alpha3Code } from 'i18n-iso-countries'
import { useTranslation } from 'react-i18next'

import { useRequiredParams } from '@percent/admin-dashboard/common/hooks'
import { getCountryFromAlpha3 } from '@percent/admin-dashboard/common/utility/getCountryFromCountryCode'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { Box, Card, FlagAvatar, Heading, IllustratedMessage, Loader, MarkdownPreview, Spacer } from '@percent/lemonade'
import { useQuery } from '@percent/utility'
import styles from './RalphOracleDetail.module.scss'

// Oracle markdown files begin with a YAML frontmatter block (e.g. `---\nversion: v7\n---`).
// Strip it so only the human-readable guidance is rendered.
const stripFrontmatter = (markdown: string) => markdown.replace(/^\s*---\r?\n[\s\S]*?\r?\n---\r?\n/, '')

export function RalphOracleDetailContainer() {
  const { country } = useRequiredParams<{ country: string }>()
  const { t } = useTranslation()
  const { ralphOracleService } = useServices()

  const { data, isPending, errorMessage, error } = useQuery(
    ['/v1/admin/ralph/oracle', country],
    ralphOracleService.getOracle,
    { country }
  )

  if (isPending) {
    return <Loader />
  }

  if (errorMessage !== null || !data) {
    const isNotFound = (error as AxiosError | null)?.response?.status === 404

    return (
      <IllustratedMessage
        illustration={isNotFound ? '404' : 'general-error'}
        title={isNotFound ? t('ralphOracle.notFound') : errorMessage ?? t('ralphOracle.errorTitle')}
      />
    )
  }

  const countryCode = data.country as Alpha3Code

  return (
    <Box>
      <Box style={{ display: 'flex', alignItems: 'center' }}>
        <FlagAvatar code3={countryCode} width="32px" />
        <Spacer size={3} axis="horizontal" />
        <Heading level="h5">{getCountryFromAlpha3(countryCode)}</Heading>
      </Box>
      <Spacer size={4} axis="vertical" />
      <Card>
        <MarkdownPreview
          className={styles.content}
          content={stripFrontmatter(data.content)}
          fontSize="default"
          testId="ralph-oracle-content"
        />
      </Card>
    </Box>
  )
}
